import React, { FunctionComponent, useState, useRef, useEffect } from 'react';
import styles from './side-nav-slider.module.scss';
import {
  ISideNavSliderItemProps,
  SideNavSliderItem
} from './side-nav-slider-item/side-nav-slider-item';
import Section from '../section/section';

export interface ISideNavSliderProps {
  navItems: ISideNavSliderItemProps[];
  content: IKontentItem[];
  header: string;
  headerPosition: string;
}

export const SideNavSlider: FunctionComponent<ISideNavSliderProps> = ({
  navItems,
  content,
  header,
  headerPosition
}) => {
  const [activeItem, setActiveItem] = useState<number>(0);
  const [contentHeight, setContentHeight] = useState<undefined | number>(
    undefined
  );
  const contentElem = useRef([]);
  const wrapperElem = useRef<null | HTMLElement>(null);

  useEffect(() => {
    const wrapper = wrapperElem.current;

    if (!wrapper) {
      return;
    }

    Promise.all(
      Array.from(wrapper.querySelectorAll('img'))
        .filter(img => !img.complete)
        .map(
          img =>
            new Promise(resolve => {
              img.onload = img.onerror = resolve;
            })
        )
    ).then(() => {
      const contentHeights = contentElem.current.map(
        (elem: HTMLElement) => +elem.getBoundingClientRect().height
      );
      const getLargest = Math.max(...contentHeights);

      setContentHeight(getLargest);
    });
  }, []);

  const changeItemHandler = (event: React.MouseEvent<HTMLElement>): void => {
    const elem = event.currentTarget as HTMLDivElement;
    const index = elem.dataset.index || 0;

    setActiveItem(+index);
  };

  return (
    <div className={styles.sideNavSlider}>
      <h2
        className={styles.header}
        style={{ textAlign: headerPosition ? headerPosition : 'center' }}
      >
        {header}
      </h2>
      <div className={styles.contentWrapper}>
        <div className={styles.navWrapper}>
          {navItems.map((item: ISideNavSliderItemProps, i: number) => (
            <SideNavSliderItem
              {...item}
              key={i}
              index={i}
              isActive={activeItem === i}
              changeItem={changeItemHandler}
            />
          ))}
        </div>
        <div className={styles.content} ref={wrapperElem}>
          {content &&
            content.map((item: IKontentItem, i: number) => (
              <div
                ref={(el: never): void => (contentElem.current[i] = el)}
                key={i}
                className={`${styles.contentItem} ${i === activeItem &&
                  styles.active}`}
                style={{ height: contentHeight }}
              >
                <Section linkedItem={item} />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
